import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Logo } from "@/components/logo";

export function LegalPage({
  title,
  effectiveDate,
  children,
}: {
  title: string;
  effectiveDate: string;
  children: React.ReactNode;
}) {
  return (
    <main className="min-h-screen bg-brand-paper-2 px-4 py-12 sm:py-16">
      <article className="relative mx-auto max-w-[820px] overflow-hidden rounded-2xl border border-brand-line bg-brand-paper shadow-[0_1px_0_var(--brand-line),0_30px_60px_-30px_oklch(0.264_0.068_255.3/0.18)]">
        <nav className="flex items-center justify-between border-b border-brand-line px-[22px] py-4 lg:px-10">
          <Link to="/" className="flex items-center gap-2 text-brand-ink no-underline">
            <Logo
              src="/capitaldart-logo.png"
              alt="CapitalDart"
              className="h-9 w-auto"
              height={36}
              trademarkClassName="text-brand-ink"
            />
          </Link>
          <Link to="/" className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink">
            ← Back to capitaldart.com
          </Link>
        </nav>
        <header className="border-b border-brand-line px-[22px] py-9 lg:px-10">
          <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-brand-accent">
            CapitalDart, LLC
          </p>
          <h1 className="mt-3 font-serif text-[36px] leading-[1.1] tracking-[-0.01em] text-brand-ink lg:text-[44px]">
            {title}
          </h1>
          <p className="mt-2 font-serif text-[15px] italic text-brand-muted">
            Effective Date: {effectiveDate}
          </p>
        </header>
        <div className="mx-auto max-w-[760px] px-[22px] py-10 text-[15.5px] leading-[1.7] text-brand-ink-2 lg:px-10 lg:py-14 [&_h1]:mt-12 [&_h1]:mb-4 [&_h1]:font-serif [&_h1]:text-[26px] [&_h1]:leading-[1.2] [&_h1]:text-brand-ink [&_h1:first-of-type]:mt-0 [&_h2]:mt-7 [&_h2]:mb-2 [&_h2]:font-serif [&_h2]:text-[19px] [&_h2]:text-brand-ink [&_p]:mb-4 [&_p]:leading-[1.7] [&_strong]:text-brand-ink">
          {children}
        </div>
        <LegalFooter />
      </article>
    </main>
  );
}

export function LegalFooter() {
  return (
    <footer className="border-t border-brand-line px-[22px] pt-[22px] pb-7 lg:px-10">
      <div className="flex flex-wrap items-center justify-between gap-3 text-[11.5px] text-brand-muted">
        <span>© CapitalDart 2026</span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Link
            to="/privacy"
            className="text-brand-muted underline decoration-brand-line underline-offset-4 hover:text-brand-ink"
          >
            Privacy
          </Link>
          <span aria-hidden>·</span>
          <Link
            to="/terms"
            className="text-brand-muted underline decoration-brand-line underline-offset-4 hover:text-brand-ink"
          >
            Terms
          </Link>
          <span aria-hidden>·</span>
          <Link
            to="/affiliate-terms"
            className="text-brand-muted underline decoration-brand-line underline-offset-4 hover:text-brand-ink"
          >
            Affiliate Terms
          </Link>
          <span aria-hidden>·</span>
          <Link
            to="/lists"
            className="text-brand-muted underline decoration-brand-line underline-offset-4 hover:text-brand-ink"
          >
            Top 10 Investor Rankings
          </Link>
          <span aria-hidden>·</span>
          <Link
            to="/"
            hash="contact"
            className="text-brand-muted underline decoration-brand-line underline-offset-4 hover:text-brand-ink"
          >
            Contact
          </Link>
        </span>
      </div>
      <p className="mt-1.5 m-0 font-serif text-[12px] italic leading-[1.4] text-brand-ink/60">
        Find the right investors. Reach the right contacts. Run a better capital raise.
      </p>
    </footer>
  );
}
